import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { apiError, API } from "@/lib/apiClient";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { SearchList } from "@/components/admin/SearchDiagnostic";
import { AdminActivity, AdminCosts, AdminAudit, AdminPricing, AdminEmptySearches, LEVEL_BADGE } from "@/components/admin/AdminPanels";
import { relativeTime } from "@/lib/format";
import { Download, Loader2, Users, CheckCircle2, UserCheck, Eye, FileText, X, Radar, Target, Trash2, UsersRound } from "lucide-react";
import AdminTeamModal from "@/components/admin/AdminTeamModal";

function fmtDate(v) {
  if (!v) return "—";
  try { return new Date(v).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return String(v); }
}

export default function Admin() {
  // v24: quien no es admin vuelve a su inicio en vez de ver el panel vacío (los datos ya estaban protegidos con 403).
  const { user } = useAuth();
  const notAdmin = !!user && !user.is_admin;
  const [rows, setRows] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [query, setQuery] = useState("");
  const [dossier, setDossier] = useState(null);
  const [dossierLoading, setDossierLoading] = useState(false);
  const [tab, setTab] = useState("usuarios");
  const [busyId, setBusyId] = useState("");
  const [toDelete, setToDelete] = useState(null);
  const [confirmEmail, setConfirmEmail] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [teamOf, setTeamOf] = useState(null);
  // v23: cuentas de las pruebas automáticas (@example.com) ocultas por defecto.
  const [showTests, setShowTests] = useState(false);
  const [testCount, setTestCount] = useState(0);

  const load = async () => {
    setLoading(true);
    try {
      const params = { include_tests: showTests };
      const [u, m] = await Promise.all([api.get("/admin/users", { params }), api.get("/admin/metrics", { params }).catch(() => ({ data: null }))]);
      setRows(u.data.users || []);
      setTestCount(u.data.test_accounts || 0);
      setMetrics(m.data);
    } catch (err) { toast.error(apiError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (!notAdmin) load(); }, [showTests, notAdmin]); // eslint-disable-line

  const exportCsv = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem("ca_token");
      const resp = await fetch(`${API}/admin/users/export`, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error("export");
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "usuarios_captaccion.csv";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV descargado.");
    } catch { toast.error("No hemos podido exportar el CSV."); }
    finally { setExporting(false); }
  };

  // v10: plan de cada cuenta (Gratis, Pro o Pro regalado) y eliminación definitiva.
  // v13: Business (cuenta madre con equipo), facturación mensual/anual y usuarios extra.
  const planValue = (r) => (r.plan === "business" ? "business" : r.plan === "pro" ? (r.plan_gift ? "pro_gift" : "pro") : "gratis");
  const changePlan = async (r, value, extra = {}) => {
    const body = value === "pro_gift" ? { plan: "pro", gift: true } : { plan: value, gift: false };
    setBusyId(r.id);
    try {
      const res = await api.post(`/admin/users/${r.id}/plan`, { ...body, ...extra });
      setRows((rs) => rs.map((x) => (x.id === r.id ? { ...x, plan: res.data.plan, plan_gift: res.data.plan_gift,
        plan_label: res.data.plan_label, searches_limit: res.data.searches_limit, billing: res.data.billing,
        extra_seats: res.data.extra_seats, team_role: res.data.team_role, team_seats: res.data.team_seats } : x)));
      toast.success(`${r.email}: ${res.data.plan_label}${res.data.plan === "gratis" || res.data.plan_gift ? "" : `, ${res.data.billing}`}.`);
      return res.data;
    } catch (err) { toast.error(apiError(err)); return null; } finally { setBusyId(""); }
  };
  const deleteUser = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await api.post(`/admin/users/${toDelete.id}/delete`, { confirm_email: confirmEmail.trim() });
      setRows((rs) => rs.filter((x) => x.id !== toDelete.id));
      toast.success(`Cuenta ${toDelete.email} eliminada.`);
      setToDelete(null);
    } catch (err) { toast.error(apiError(err)); } finally { setDeleting(false); }
  };

  const openDossier = async (u) => {
    setDossierLoading(true); setDossier({ loading: true, user: u });
    try {
      const r = await api.get(`/admin/users/${u.id}`);
      setDossier(r.data);
    } catch (err) { toast.error(apiError(err)); setDossier(null); }
    finally { setDossierLoading(false); }
  };

  const viewAsUser = async (u) => {
    try {
      const r = await api.post("/admin/impersonate", { user_id: u.id });
      localStorage.setItem("ca_admin_token", localStorage.getItem("ca_token"));
      localStorage.setItem("ca_token", r.data.token);
      localStorage.setItem("ca_impersonation", JSON.stringify({ email: r.data.user.email, expires_at: r.data.expires_at }));
      window.location.href = "/app/hoy";
    } catch (err) { toast.error(apiError(err)); }
  };

  const stats = metrics ? [
    { label: "Registrados", value: metrics.users, icon: Users },
    { label: "Onboarding", value: metrics.onboarded, icon: CheckCircle2 },
    { label: "Resumen aprobado", value: metrics.summary_approved, icon: UserCheck },
    { label: "Búsquedas", value: metrics.search_jobs, icon: Radar },
    { label: "Opp. guardadas", value: metrics.opportunities_saved, icon: Target },
    { label: "Prospectos", value: metrics.prospects, icon: Users },
  ] : [];

  const filtered = rows.filter((r) => !query || (r.email || "").toLowerCase().includes(query.toLowerCase()) || (r.name || "").toLowerCase().includes(query.toLowerCase()));

  if (notAdmin) return <Navigate to="/app/hoy" replace />;
  return (
    <div className="space-y-8" data-testid="admin-page">
      <PageHeader title="Admin · Centro de control" subtitle="Usuarios, actividad y diagnóstico de CaptAcción." testId="admin-header"
        action={<button onClick={exportCsv} disabled={exporting || loading} data-testid="admin-export-csv" className="ca-btn-gold ca-btn-sm disabled:opacity-60">{exporting ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Exportar CSV</button>} />

      {testCount > 0 && (
        <label className="flex w-fit cursor-pointer items-center gap-2 text-xs text-[var(--ca-subtle)]" data-testid="admin-show-tests">
          <input type="checkbox" checked={showTests} onChange={(e) => setShowTests(e.target.checked)} />
          {showTests ? `Incluyendo ${testCount} cuentas de prueba (@example.com)` : `Mostrar ${testCount} cuentas de prueba (@example.com), ocultas en usuarios y métricas`}
        </label>
      )}

      {/* P3-A: dashboard global */}
      <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-6" data-testid="admin-metrics">
        {stats.map((s) => (
          <div key={s.label} className="rounded-xl border border-[var(--ca-line)] p-4" style={{ background: "var(--ca-surface)" }} data-testid={`admin-stat-${s.label}`}>
            <div className="flex items-center gap-2 text-[var(--ca-subtle)]"><s.icon size={15} style={{ color: "var(--ca-gold)" }} /><span className="text-[10px] font-semibold uppercase tracking-wide">{s.label}</span></div>
            <p className="mt-1.5 text-2xl font-extrabold tracking-tight text-[var(--ca-text)]">{s.value ?? "—"}</p>
          </div>
        ))}
      </div>

      {/* v9: pestañas del centro de control */}
      <div className="flex flex-wrap gap-2" role="tablist" data-testid="admin-tabs">
        {[["usuarios", "Usuarios"], ["actividad", "Contactos y planes"], ["costes", "Costes"], ["vacias", "Búsquedas vacías"], ["precios", "Precios y ofertas"], ["historial", "Historial de admin"]].map(([k, l]) => (
          <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`}
            className={`rounded-full border px-4 py-1.5 text-xs font-semibold transition-colors ${tab === k ? "border-[var(--ca-gold)] text-[var(--ca-text)]" : "border-[var(--ca-line)] text-[var(--ca-subtle)] hover:text-[var(--ca-text)]"}`}
            style={tab === k ? { background: "rgba(224,185,112,.10)" } : undefined}>{l}</button>
        ))}
      </div>

      {tab === "actividad" && <AdminActivity metrics={metrics} />}
      {tab === "costes" && <AdminCosts />}
      {tab === "historial" && <AdminAudit />}
      {tab === "precios" && <AdminPricing />}
      {tab === "vacias" && <AdminEmptySearches />}

      {tab === "usuarios" && (<>
      <input value={query} onChange={(e) => setQuery(e.target.value)} data-testid="admin-search" placeholder="Buscar por email o nombre…"
        className="w-full rounded-lg border border-[var(--ca-line)] px-4 py-2.5 text-sm" style={{ background: "var(--ca-surface)", color: "var(--ca-text)" }} />

      <div className="overflow-hidden rounded-xl border border-[var(--ca-line)]" style={{ background: "var(--ca-surface)" }}>
        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin" size={24} style={{ color: "var(--ca-gold)" }} /></div>
        ) : filtered.length === 0 ? (
          <div className="py-20 text-center text-sm text-[var(--ca-muted)]" data-testid="admin-empty">Sin resultados.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm" data-testid="admin-users-table">
              <thead><tr className="border-b border-[var(--ca-line)] text-[11px] uppercase tracking-wide text-[var(--ca-subtle)]">
                <th className="px-3 py-3 font-semibold">Usuario</th><th className="px-3 py-3 font-semibold">Alta</th>
                <th className="px-3 py-3 font-semibold">Plan</th><th className="px-3 py-3 text-right font-semibold">Contactos</th>
                <th className="px-3 py-3 font-semibold">Perfil</th><th className="px-3 py-3 font-semibold">Actividad</th>
                <th className="px-3 py-3 text-right font-semibold">Acciones</th>
              </tr></thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id} className="border-b border-[var(--ca-line)] last:border-0" data-testid={`admin-user-row-${r.email}`}>
                    <td className="max-w-[260px] px-3 py-2.5">
                      <p className="truncate font-semibold text-[var(--ca-text)]">{r.name || "—"}{r.is_admin && <span className="ml-2 rounded-md border border-[var(--ca-line-2)] px-1.5 py-0.5 text-[10px] font-bold text-[var(--ca-gold)]">ADMIN</span>}</p>
                      <p className="truncate text-xs text-[var(--ca-muted)]" title={r.email}>{r.email}</p>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-xs">
                      <p className="text-[var(--ca-muted)]">{fmtDate(r.created_at)}</p>
                      <p className={r.onboarding_completed ? "text-[var(--ca-gold-hi)]" : "text-[var(--ca-subtle)]"}>{r.onboarding_completed ? "Onboarding hecho" : "Onboarding pendiente"}</p>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5" data-testid={`admin-user-plan-${r.email}`}>
                      {r.is_demo ? <span className="text-xs text-[var(--ca-subtle)]">Demo</span> : r.team_role === "member" ? (
                        <p className="text-xs font-semibold text-[var(--ca-text)]" title={r.team_owner_email}>
                          Business (equipo)<span className="block font-normal text-[var(--ca-subtle)]">de {r.team_owner_email}</span>
                        </p>
                      ) : (
                        <select value={planValue(r)} onChange={(e) => changePlan(r, e.target.value)} disabled={busyId === r.id}
                          data-testid={`admin-plan-select-${r.email}`} aria-label={`Plan de ${r.email}`}
                          className="rounded-md border border-[var(--ca-line)] px-2 py-1 text-xs font-semibold text-[var(--ca-text)]" style={{ background: "var(--ca-surface)" }}>
                          <option value="gratis">Gratis</option>
                          <option value="pro">Pro</option>
                          <option value="pro_gift">Pro (regalado)</option>
                          <option value="business">Business</option>
                        </select>
                      )}
                      {!r.is_demo && r.team_role !== "member" && (r.plan === "business" || (r.plan === "pro" && !r.plan_gift)) && (
                        <select value={r.billing || "mensual"} onChange={(e) => changePlan(r, planValue(r), { billing: e.target.value })} disabled={busyId === r.id}
                          data-testid={`admin-billing-select-${r.email}`} aria-label={`Facturación de ${r.email}`}
                          className="ml-1 rounded-md border border-[var(--ca-line)] px-2 py-1 text-xs text-[var(--ca-text)]" style={{ background: "var(--ca-surface)" }}>
                          <option value="mensual">Mensual</option>
                          <option value="anual">Anual</option>
                        </select>
                      )}
                      {r.team_role === "owner" && (
                        <button type="button" onClick={() => setTeamOf(r)} data-testid={`admin-team-${r.email}`}
                          className="mt-1 flex items-center gap-1 text-[11px] font-semibold text-[var(--ca-gold)] hover:underline">
                          <UsersRound size={12} /> Equipo {1 + (r.team_members || 0)}/{r.team_seats || 10}
                        </button>
                      )}
                      <p className="mt-1 text-[11px] text-[var(--ca-subtle)]">{r.searches_used ?? 0}/{r.searches_limit ?? "—"} búsquedas</p>
                      {r.plan_interest && r.plan === "gratis" && (
                        <p className="mt-0.5 text-[11px] font-semibold text-[var(--ca-gold)]" data-testid={`admin-plan-interest-${r.email}`}>
                          Quiere {r.plan_interest === "business" ? "Business" : "Pro"}
                        </p>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-right font-semibold text-[var(--ca-text)]">{r.prospect_count}</td>
                    <td className="px-3 py-2.5" data-testid={`admin-user-profile-${r.email}`}>
                      {r.profile_level_key ? (
                        <span title={`@${r.profile_username} · ${r.profile_level}`} className="whitespace-nowrap text-xs font-semibold" style={{ color: (LEVEL_BADGE[r.profile_level_key] || {}).color }}>
                          {(LEVEL_BADGE[r.profile_level_key] || {}).label || r.profile_level}{typeof r.profile_score === "number" ? ` · ${r.profile_score}` : ""}
                        </span>
                      ) : <span className="whitespace-nowrap text-xs text-[var(--ca-subtle)]">Sin analizar</span>}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-xs text-[var(--ca-muted)]" title={r.last_activity_at || ""}>{r.last_activity_at ? relativeTime(r.last_activity_at) : "—"}</td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center justify-end gap-1.5">
                        <button onClick={() => openDossier(r)} title="Dossier" aria-label={`Dossier de ${r.email}`} data-testid={`admin-dossier-${r.email}`} className="rounded-md border border-[var(--ca-line)] p-1.5 text-[var(--ca-text)] hover:border-[var(--ca-gold)]"><FileText size={14} /></button>
                        <button onClick={() => viewAsUser(r)} title="Ver como (solo lectura)" aria-label={`Ver como ${r.email}`} data-testid={`admin-viewas-${r.email}`} className="rounded-md border border-[var(--ca-line)] p-1.5 text-[var(--ca-text)] hover:border-[var(--ca-gold)]"><Eye size={14} /></button>
                        {!r.is_admin && !r.is_demo && (
                          <button onClick={() => { setToDelete(r); setConfirmEmail(""); }} title="Eliminar cuenta" aria-label={`Eliminar ${r.email}`} data-testid={`admin-delete-${r.email}`} className="rounded-md border border-[var(--ca-line)] p-1.5 text-[#F1707A] hover:border-[#F1707A]"><Trash2 size={14} /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      </>)}

      {teamOf && (
        <AdminTeamModal owner={teamOf} onClose={(changed) => { setTeamOf(null); if (changed) load(); }}
          onExtraSeats={async (n) => { const d = await changePlan(teamOf, "business", { extra_seats: n }); if (d) setTeamOf((t) => ({ ...t, extra_seats: d.extra_seats })); return d; }} />
      )}

      {toDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="admin-delete-modal" onClick={() => !deleting && setToDelete(null)}>
          <div className="w-full max-w-md rounded-xl border border-[var(--ca-line)] p-6" style={{ background: "var(--ca-surface)" }} onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-[var(--ca-text)]">Eliminar cuenta</h3>
            <p className="mt-2 text-sm text-[var(--ca-muted)]">
              Se borran la cuenta <b className="text-[var(--ca-text)]">{toDelete.email}</b> y todos sus datos: contactos, búsquedas, diagnóstico y actividad.
              <b className="text-[#F1707A]"> No se puede deshacer.</b>
            </p>
            <label className="mt-4 block text-xs font-semibold text-[var(--ca-subtle)]" htmlFor="confirm-delete">Escribe su correo para confirmar</label>
            <input id="confirm-delete" value={confirmEmail} onChange={(e) => setConfirmEmail(e.target.value)} autoComplete="off"
              data-testid="admin-delete-confirm-input" placeholder={toDelete.email}
              className="mt-1 w-full rounded-lg border border-[var(--ca-line)] px-3 py-2 text-sm" style={{ background: "var(--ca-surface)", color: "var(--ca-text)" }} />
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setToDelete(null)} disabled={deleting} className="rounded-md border border-[var(--ca-line)] px-4 py-2 text-sm text-[var(--ca-text)]">Cancelar</button>
              <button onClick={deleteUser} data-testid="admin-delete-confirm"
                disabled={deleting || confirmEmail.trim().toLowerCase() !== (toDelete.email || "").toLowerCase()}
                className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-bold text-white disabled:opacity-40" style={{ background: "#C9444F" }}>
                {deleting && <Loader2 size={14} className="animate-spin" />} Eliminar para siempre
              </button>
            </div>
          </div>
        </div>
      )}

      {dossier && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 p-4" data-testid="admin-dossier-modal" onClick={() => setDossier(null)}>
          <div className="my-8 w-full max-w-3xl rounded-xl border border-[var(--ca-line)] p-6" style={{ background: "var(--ca-surface)" }} onClick={(e) => e.stopPropagation()}>
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-bold text-[var(--ca-text)]">Dossier · {dossier.user?.email || dossier.user?.name}</h3>
              <button onClick={() => setDossier(null)} data-testid="admin-dossier-close" className="text-[var(--ca-subtle)] hover:text-[var(--ca-text)]"><X size={18} /></button>
            </div>
            {dossierLoading || dossier.loading ? (
              <div className="flex justify-center py-12"><Loader2 className="animate-spin" size={22} style={{ color: "var(--ca-gold)" }} /></div>
            ) : (
              <div className="space-y-4 text-sm text-[var(--ca-text)]">
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <Kpi label="Perfiles" value={(dossier.profiles || []).length} />
                  <Kpi label="Búsquedas" value={(dossier.searches || []).length} />
                  <Kpi label="Prospectos" value={(dossier.prospects || []).length} />
                  <Kpi label="Eventos" value={(dossier.events || []).length} />
                </div>
                <Section title="Negocio">
                  <p>{dossier.business_brain?.business_name || "—"} · {dossier.business_brain?.business_type || "—"}</p>
                  <p className="text-[var(--ca-muted)]">{dossier.business_brain?.offer || "Sin oferta descrita."}</p>
                  <p className="text-xs text-[var(--ca-subtle)]">Resumen: {dossier.business_brain?.summary_approved ? "aprobado" : (dossier.business_brain?.summary_status || "—")}</p>
                </Section>
                <Section title="Objetivos">{(dossier.objectives || []).join(", ") || "—"}</Section>
                <Section title="Perfiles">{(dossier.profiles || []).map((p) => p.sector || p.name).filter(Boolean).join(" · ") || "—"}</Section>
                <Section title="Búsquedas"><SearchList searches={dossier.searches} /></Section>
                <Section title={`Últimos eventos (${(dossier.events || []).length})`}>
                  <div className="max-h-48 space-y-1 overflow-y-auto" data-testid="admin-dossier-events">
                    {(dossier.events || []).slice(0, 30).map((e, i) => (
                      <div key={i} className="flex justify-between text-xs"><span>{e.event_type}</span><span className="text-[var(--ca-subtle)]">{fmtDate(e.ts)}</span></div>
                    ))}
                    {(dossier.events || []).length === 0 && <p className="text-xs text-[var(--ca-subtle)]">Sin eventos registrados.</p>}
                  </div>
                </Section>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value }) {
  return (
    <div className="rounded-lg border border-[var(--ca-line)] p-3 text-center">
      <p className="text-xl font-extrabold text-[var(--ca-text)]">{value}</p>
      <p className="text-[10px] uppercase tracking-wide text-[var(--ca-subtle)]">{label}</p>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="rounded-lg border border-[var(--ca-line)] p-3">
      <p className="mb-1 text-xs font-bold uppercase tracking-wide text-[var(--ca-subtle)]">{title}</p>
      <div>{children}</div>
    </div>
  );
}
